import { useState, useEffect } from 'react';
import { useSubscription } from './useSubscription';
import { useUsageDashboard } from './useUsageDashboard';
import { getInvoices } from '../api/payments';
import { listPaymentMethods } from '../api/paymentMethods';

export type MenuStatus = {
  type: 'ok' | 'warning' | 'error' | 'pending' | 'new';
  count?: number;
};

export type MenuStatuses = Record<string, MenuStatus | null>;

export const useMenuStatuses = () => {
  const [statuses, setStatuses] = useState<MenuStatuses>({});
  const [loading, setLoading] = useState(true);

  const { subscription } = useSubscription();
  const { quotas, alerts, trial } = useUsageDashboard();

  useEffect(() => {
    const calculateStatuses = async () => {
      const newStatuses: MenuStatuses = {};

      try {
        // 1. Statut Abonnement
        if (subscription) {
          let subStatus: MenuStatus | null = null;

          // Trial expiré
          if (trial?.is_expired) {
            subStatus = { type: 'error', count: 1 };
          }
          // Trial bientôt expiré
          else if (trial?.is_expiring_soon) {
            subStatus = { type: 'warning', count: 1 };
          }
          // Statut de paiement
          else if (subscription.status === 'past_due') {
            subStatus = { type: 'error', count: 1 };
          }
          else if (subscription.status === 'incomplete') {
            subStatus = { type: 'warning', count: 1 };
          }
          else if (subscription.status === 'active') {
            subStatus = { type: 'ok' };
          }

          newStatuses['subscription'] = subStatus;
        }

        // 2. Statut Utilisation & Quotas
        if (alerts && alerts.length > 0) {
          const criticalAlerts = alerts.filter(a => a.severity === 'high').length;
          const warningAlerts = alerts.filter(a => a.severity === 'medium').length;

          if (criticalAlerts > 0) {
            newStatuses['usage'] = { type: 'error', count: criticalAlerts };
          } else if (warningAlerts > 0) {
            newStatuses['usage'] = { type: 'warning', count: warningAlerts };
          } else {
            newStatuses['usage'] = { type: 'ok' };
          }
        } else {
          newStatuses['usage'] = { type: 'ok' };
        }

        // 3. Statut Factures (seulement pour les plans payants)
        if (subscription && subscription.plan !== 'free') {
          try {
            const invoicesData = await getInvoices(50);
            const unpaidInvoices = invoicesData.invoices.filter(
              inv => inv.status === 'open' || inv.status === 'uncollectible'
            );

            if (unpaidInvoices.length > 0) {
              newStatuses['invoices'] = { type: 'pending', count: unpaidInvoices.length };
            } else {
              newStatuses['invoices'] = { type: 'ok' };
            }
          } catch (error) {
            console.error('Error fetching invoices for status:', error);
            newStatuses['invoices'] = null;
          }
        }

        // 4. Statut Moyens de paiement (seulement pour les plans payants)
        if (subscription && subscription.plan !== 'free') {
          try {
            const paymentMethods = await listPaymentMethods();
            const expiredCards = paymentMethods.filter(pm => {
              if (pm.card) {
                const now = new Date();
                const expMonth = pm.card.exp_month;
                const expYear = pm.card.exp_year;
                const expDate = new Date(expYear, expMonth - 1);
                return expDate < now;
              }
              return false;
            });

            if (expiredCards.length > 0) {
              newStatuses['payment-methods'] = { type: 'error', count: expiredCards.length };
            } else {
              newStatuses['payment-methods'] = { type: 'ok' };
            }
          } catch (error) {
            console.error('Error fetching payment methods for status:', error);
            newStatuses['payment-methods'] = null;
          }
        }

        // 5. Autres sections OK par défaut
        newStatuses['connections'] = { type: 'ok' };
        newStatuses['users'] = { type: 'ok' };
        newStatuses['teams'] = { type: 'ok' };
        newStatuses['groups'] = { type: 'ok' };
        newStatuses['permissions'] = { type: 'ok' };
        newStatuses['devops'] = { type: 'ok' };
        newStatuses['byoo'] = { type: 'ok' };
        newStatuses['white-label'] = { type: 'ok' };
        newStatuses['admin'] = { type: 'ok' };

        setStatuses(newStatuses);
      } catch (error) {
        console.error('Error calculating menu statuses:', error);
      } finally {
        setLoading(false);
      }
    };

    calculateStatuses();
  }, [subscription, quotas, alerts, trial]);

  return { statuses, loading };
};
